import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import { formatARS } from '../../utils/formatCurrency';
import { PACK_SIZE, receiptColorStyle } from '../../utils/warehouseStock';
import { BagWeight, useShowWeight } from './WeightToggle';
import { bagWeightGrams, colorKeyFromLabel, parseMeasure } from '../../utils/bagWeight';

export interface ClientPriceEntry { widthCm: number; lengthCm: number; color: string; microns: number; saleNetUnit: number; vatRate: number; unitsPerPackage: number; }

export interface ReceiptItem {
  productName: string;
  productColor: string;
  productMicrons?: number | null;
  quantity: number;
  packs: number;
  looseBags: number;
  deduct?: boolean;
  itemNote?: string;
  saleNetUnit?: number;
  vatRate?: number;
}

// Cache por cliente: un remito listado muchas veces no vuelve a pedir la misma lista de precios.
const priceCache = new Map<number, Promise<ClientPriceEntry[]>>();
const loadClientPrices = (clientId: number) => {
  if (!priceCache.has(clientId)) {
    priceCache.set(clientId, apiService.getClientPrices(clientId).then((r) => r.prices || []).catch(() => { priceCache.delete(clientId); return []; }));
  }
  return priceCache.get(clientId)!;
};

export const useClientPriceLists = (clientIds: Array<number | null | undefined>): Record<number, ClientPriceEntry[]> => {
  const [lists, setLists] = useState<Record<number, ClientPriceEntry[]>>({});
  const key = Array.from(new Set(clientIds.filter((id): id is number => !!id))).sort((a, b) => a - b).join(',');
  useEffect(() => {
    let active = true;
    const ids = key ? key.split(',').map(Number) : [];
    Promise.all(ids.map(async (id) => [id, await loadClientPrices(id)] as const)).then((entries) => {
      if (active) setLists(Object.fromEntries(entries));
    });
    return () => { active = false; };
  }, [key]);
  return lists;
};

export const findClientPrice = (prices: ClientPriceEntry[] | undefined, widthCm: number, lengthCm: number, colorLabel: string, microns: number) => {
  if (!prices?.length) return undefined;
  const color = colorKeyFromLabel(colorLabel);
  const candidates = prices.filter((p) => Number(p.widthCm) === widthCm && Number(p.lengthCm) === lengthCm && p.color === color);
  return candidates.find((p) => Number(p.microns || 0) === microns) || (candidates.length === 1 && !microns ? candidates[0] : undefined);
};

export const estimateItem = (item: ReceiptItem, prices?: ClientPriceEntry[]) => {
  const measure = parseMeasure(item.productName);
  const microns = Number(item.productMicrons || 0);
  const weight = measure ? bagWeightGrams(measure.widthCm, measure.lengthCm, microns) : null;
  // El precio guardado en el remito es el que se usó al entregar; si no hay, el de la lista actual.
  if (Number(item.saleNetUnit) > 0) {
    const net = Number(item.saleNetUnit) * PACK_SIZE;
    return { weight, packNet: net, packGross: net * (1 + Number(item.vatRate ?? 21) / 100), priceSource: 'Precio guardado en el remito' };
  }
  const price = measure ? findClientPrice(prices, measure.widthCm, measure.lengthCm, item.productColor, microns) : undefined;
  if (price && Number(price.saleNetUnit) > 0) {
    const units = Number(price.unitsPerPackage) || PACK_SIZE;
    const net = Number(price.saleNetUnit) * units;
    return { weight, packNet: net, packGross: net * (1 + Number(price.vatRate ?? 21) / 100), priceSource: `Lista de precios del cliente (${units} bolsas por paquete)` };
  }
  return { weight, packNet: null, packGross: null, priceSource: '' };
};

export const PackPrice: React.FC<{ gross: number | null; net: number | null; source?: string; missing?: string }> = ({ gross, net, source, missing = 'sin precio' }) => (
  gross == null ? <span className="text-gray-400">{missing}</span> : (
    <span title={source} className="whitespace-nowrap">
      <span className="font-semibold text-gray-900">{formatARS(gross)}</span>
      {net != null && <span className="block text-[10px] text-gray-500">s/IVA {formatARS(net)}</span>}
    </span>
  )
);

const ReceiptItemsTable: React.FC<{ items: ReceiptItem[]; prices?: ClientPriceEntry[]; className?: string }> = ({ items, prices, className = 'overflow-x-auto rounded-lg border border-gray-200' }) => {
  const showWeight = useShowWeight();
  return (
  <div className={className}>
    <table className="min-w-full text-xs">
      <thead className="bg-gray-50 text-gray-500">
        <tr>
          <th className="px-3 py-1.5 text-left">Medida</th>
          <th className="px-3 py-1.5 text-left">Color</th>
          <th className="px-3 py-1.5 text-right">Packs</th>
          <th className="px-3 py-1.5 text-right">Bolsas</th>
          {showWeight && <th className="px-3 py-1.5 text-right" title="Ancho × largo × micrones × 2 × 0,92 ÷ 10.000. Debajo, el pack completo.">Peso est. bolsa / pack</th>}
          <th className="px-3 py-1.5 text-right" title="Con IVA; debajo, sin IVA">Precio est. pack</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {items.map((item, index) => {
          const estimate = estimateItem(item, prices);
          return (
            <tr key={`${item.productName}-${item.productColor}-${index}`}>
              <td className="px-3 py-1.5 font-medium">
                {item.productName}{item.productMicrons ? <span className="font-normal text-gray-400"> · {item.productMicrons}μ</span> : null}
                {item.deduct === false && <span title="No descuenta de Stock propio" className="ml-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">otro prov.</span>}
                {item.itemNote && <span className="ml-1 block text-[10px] font-normal italic text-gray-500">{item.itemNote}</span>}
              </td>
              <td className="px-3 py-1.5"><span className="inline-flex min-w-16 justify-center rounded px-2 py-0.5 font-semibold" style={receiptColorStyle(item.productColor)}>{item.productColor}</span></td>
              <td className="px-3 py-1.5 text-right">{item.packs}{item.looseBags ? ` + ${item.looseBags}` : ''}</td>
              <td className="px-3 py-1.5 text-right">{item.quantity.toLocaleString('es-AR')}</td>
              {showWeight && <td className="whitespace-nowrap px-3 py-1.5 text-right"><BagWeight grams={estimate.weight} /></td>}
              <td className="px-3 py-1.5 text-right"><PackPrice gross={estimate.packGross} net={estimate.packNet} source={estimate.priceSource} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

export default ReceiptItemsTable;
